// @mui material components
import { Icon } from "@mui/material";

// Custom UI components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInputNumber from "components/SoftInputNumberUpgrade";

// Utils
import { formatMoneyPunto } from "utils/formatters";
import { getUproColor } from "utils/colors";

// Constantes del sistema
export const SISTEMA_COLORS = {
    Compras: "uproPink",
    Alumnos: "uproGreen",
    Comedor: "uproYellow",
};

export const SISTEMAS_NAME = {
    Compras: "SGU",
    Alumnos: "Académica",
    Comedor: "Comedor",
    null: "Particular",
};

export const TIPO_USER_ENUM = {
    Empleado: "Empleado",
    Docente: "Docente",
    DOCENTE: "Docente",
    Alumno: "Alumno",
    Personaldeapoyo: "Personal de apoyo",
    Administrador: "Administrador",
    Externo: "Externo",
    Gestionacademica: "Gestión Académica",
    Particular: "Particular",
};

const widths = {
    appUsuario: "17%",
    sede: "16%",
    convenio:  "20%",
    tipoUsuario: "20%",
    precio: "16%",
};

// Configuración de columnas para DataTableCustom
export const getColumns = () => [
    {
        field: "aplicacion",
        headerName: "Aplicación",
        width: widths.convenio,
        align: "left",
        sortable: true,
        renderHeader: (column, isCurrentSort, direction) => (
            <SoftBox
                display="flex"
                alignItems="center"
                justifyContent="center"
                gap={1}
                sx={{
                    width: "100%",
                    overflow: "hidden",
                    minWidth: 0,
                }}
            >
                <SoftBox display="flex" alignItems="center" gap={1}>
                    <Icon fontSize="small" color="primary" sx={{ flexShrink: 0 }}>apps</Icon>
                    <SoftTypography
                        variant="caption"
                        fontWeight="bold"
                        textTransform="uppercase"
                        color="dark"
                        sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            flex: 1,
                            minWidth: 0,
                        }}
                        title={column.headerName}
                    >
                        {column.headerName}
                    </SoftTypography>
                </SoftBox>
                {isCurrentSort && direction ? (
                    <Icon
                        fontSize="small"
                        sx={{
                            color: direction === 'asc' ? 'primary.main' : 'primary.main',
                            opacity: 0.8
                        }}
                    >
                        {direction === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}
                    </Icon>
                ) : (
                    <Icon
                        fontSize="small"
                        sx={{
                            color: 'text.secondary',
                            opacity: 0.4,
                            '&:hover': { opacity: 0.8 }
                        }}
                    >
                        import_export
                    </Icon>
                )}
            </SoftBox>
        ),
        customSortValue: (item) => SISTEMAS_NAME[item?.appUsuario] || "Particular",
        renderCell: (item) => (
            <SoftBox
                display="flex"
                alignItems="center"
                gap={1}
                sx={{
                    width: "100%",
                    overflow: "hidden",
                    minWidth: 0,
                }}
            >
                <SoftBox
                    bgColor={getUproColor(SISTEMA_COLORS[item.appUsuario])}
                    color="white"
                    borderRadius="md"
                    p={0.5}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    width={widths.appUsuario}
                    height="30px"
                    sx={{ flexShrink: 0 }}
                >
                    <Icon fontSize="small">apps</Icon>
                </SoftBox>
                <SoftTypography
                    variant="button"
                    fontWeight="regular"
                    color="secondary"
                    sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        flex: 1,
                        minWidth: 0,
                    }}
                    title={SISTEMAS_NAME[item?.appUsuario] || "Particular"}
                >
                    {SISTEMAS_NAME[item?.appUsuario] || "Particular"}
                </SoftTypography>
            </SoftBox>
        ),
    },
    {
        field: "sede",
        headerName: "Sede",
        align: "center",
        sortable: true,
        width: widths.sede,
        renderHeader: (column, isCurrentSort, direction) => (
            <SoftBox
                display="flex"
                alignItems="center"
                gap={1}
                sx={{
                    width: "100%",
                    overflow: "hidden",
                    minWidth: 0,
                }}
            >
                <SoftTypography
                    variant="caption"
                    fontWeight="bold"
                    textTransform="uppercase"
                    color="dark"
                    sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        flex: 1,
                        minWidth: 0,
                    }}
                    ml={2}
                    title={column.headerName}
                >
                    {column.headerName}
                </SoftTypography>
                {isCurrentSort && direction ? (
                    <Icon
                        fontSize="small"
                        sx={{
                            color: direction === 'asc' ? 'primary.main' : 'primary.main',
                            opacity: 0.8
                        }}
                    >
                        {direction === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}
                    </Icon>
                ) : (
                    <Icon
                        fontSize="small"
                        sx={{
                            color: 'text.secondary',
                            opacity: 0.4,
                            '&:hover': { opacity: 0.8 }
                        }}
                    >
                        import_export
                    </Icon>
                )}
            </SoftBox>
        ),
        customSortValue: (item) => item?.sede?.nombre || "",
        renderCell: (item) => (
            <SoftBox
                display="flex"
                alignItems="center"
                justifyContent="center"
                gap={1}
                sx={{
                    width: "100%",
                    overflow: "hidden",
                    minWidth: 0,
                }}
            >
                <SoftTypography
                    variant="button"
                    fontWeight="regular"
                    color="secondary"
                    sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        flex: 1,
                        minWidth: 0,
                    }}
                >
                    {item?.sede?.nombre || "Particular"}
                </SoftTypography>
            </SoftBox>
        ),
    },
    {
        field: "convenio",
        headerName: "Convenio",
        width: widths.convenio,
        align: "right",
        sortable: true,
        renderHeader: (column, isCurrentSort, direction) => (
            <SoftBox
                display="flex"
                alignItems="center"
                gap={1}
                sx={{
                    width: "100%",
                    overflow: "hidden",
                    minWidth: 0,
                }}
            >
                <SoftTypography
                    variant="caption"
                    fontWeight="bold"
                    textTransform="uppercase"
                    color="dark"
                    ml={2}
                    sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        flex: 1,
                        minWidth: 0,
                    }}
                    title={column.headerName}
                >
                    {column.headerName}
                </SoftTypography>
                {isCurrentSort && direction ? (
                    <Icon
                        fontSize="small"
                        sx={{
                            color: direction === 'asc' ? 'primary.main' : 'primary.main',
                            opacity: 0.8
                        }}
                    >
                        {direction === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}
                    </Icon>
                ) : (
                    <Icon
                        fontSize="small"
                        sx={{
                            color: 'text.secondary',
                            opacity: 0.4,
                            '&:hover': { opacity: 0.8 }
                        }}
                    >
                        import_export
                    </Icon>
                )}
            </SoftBox>
        ),
        customSortValue: (item) => item?.convenio?.nombreInstitucion || "",
        renderCell: (item) => (
            <SoftBox
                display="flex"
                alignItems="center"
                gap={1}
                sx={{
                    width: "100%",
                    overflow: "hidden",
                    minWidth: 0,
                }}
            >
                <SoftTypography
                    variant="button"
                    fontWeight="regular"
                    color="secondary"
                    sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        flex: 1,
                        minWidth: 0,
                    }}
                >
                    {item?.convenio?.nombreInstitucion || "-"}
                </SoftTypography>
            </SoftBox>
        ),
    },
    {
        field: "tipoUsuario",
        headerName: "Tipo de usuario",
        align: "left",
        width: widths.tipoUsuario,
        sortable: true,
        renderHeader: (column, isCurrentSort, direction) => (
            <SoftBox
                display="flex"
                alignItems="center"
                justifyContent="center"
                gap={1}
                sx={{
                    width: "100%",
                    overflow: "hidden",
                    minWidth: 0,
                }}
            >
                <SoftTypography
                    variant="caption"
                    fontWeight="bold"
                    textTransform="uppercase"
                    color="dark"
                    sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        flex: 1,
                        minWidth: 0,
                    }}
                    ml={2}
                    title={column.headerName}
                >
                    {column.headerName}
                </SoftTypography>
                {isCurrentSort && direction ? (
                    <Icon
                        fontSize="small"
                        sx={{
                            color: direction === 'asc' ? 'primary.main' : 'primary.main',
                            opacity: 0.8
                        }}
                    >
                        {direction === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}
                    </Icon>
                ) : (
                    <Icon
                        fontSize="small"
                        sx={{
                            color: 'text.secondary',
                            opacity: 0.4,
                            '&:hover': { opacity: 0.8 }
                        }}
                    >
                        import_export
                    </Icon>
                )}
            </SoftBox>
        ),
        customSortValue: (item) => TIPO_USER_ENUM[item?.tipoUsuario] || "",
        renderCell: (item) => (
            <SoftBox
                display="flex"
                alignItems="center"
                gap={1}
                sx={{
                    width: "100%",
                    overflow: "hidden",
                    minWidth: 0,
                }}
            >
                <SoftTypography
                    variant="button"
                    fontWeight="regular"
                    color="secondary"
                    sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        flex: 1,
                        minWidth: 0,
                    }}
                >
                    {TIPO_USER_ENUM[item?.tipoUsuario] || "-"}
                </SoftTypography>
            </SoftBox>
        ),
    },
    {
        field: "precio",
        headerName: "Precio",
        align: "center",
        width: widths.precio,
        editable: true,
        sortable: true,
        customSortValue: (item) => parseFloat(item.precio) || 0,
        renderHeader: (column, isCurrentSort, direction) => (
            <SoftBox
                display="flex"
                alignItems="center"
                gap={1}
                sx={{
                    width: "100%",
                    overflow: "hidden",
                    minWidth: 0,
                }}
            >
                <SoftTypography
                    variant="caption"
                    fontWeight="bold"
                    textTransform="uppercase"
                    color="dark"
                    ml={2}
                    sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        flex: 1,
                        minWidth: 0,
                    }}
                    title={column.headerName}
                >
                    {column.headerName}
                </SoftTypography>
                {isCurrentSort && direction ? (
                    <Icon
                        fontSize="small"
                        sx={{
                            color: direction === 'asc' ? 'primary.main' : 'primary.main',
                            opacity: 0.8
                        }}
                    >
                        {direction === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}
                    </Icon>
                ) : (
                    <Icon
                        fontSize="small"
                        sx={{
                            color: 'text.secondary',
                            opacity: 0.4,
                            '&:hover': { opacity: 0.8 }
                        }}
                    >
                        import_export
                    </Icon>
                )}
            </SoftBox>
        ),
        renderCell: (item) => (
            <SoftBox display="flex" alignItems="center" gap={.5}>
                <Icon fontSize="small" color="secondary" sx={{ flexShrink: 0 }}>attach_money</Icon>
                <SoftTypography variant="button" fontWeight="regular" color="secondary">
                    {formatMoneyPunto(item.precio)}
                </SoftTypography>
            </SoftBox>
        ),
    },
];

// Función para renderizar la celda de edición del precio
export const getRenderEditCell = (tempPrecio, setTempPrecio) => (column, item) => {
    if (column.field === "precio") {
        return (
            <SoftInputNumber
                placeholder="Nuevo precio"
                variant="outlined"
                name="precio"
                onChange={(e) => setTempPrecio(e.target.value)}
                value={tempPrecio}
                size="small"
                sx={{ width: "120px" }}
                autoFocus
            />
        );
    }
    return null;
};

// Configuración de acciones
export const getActions = (handleEditClick) => [
    {
        icon: "edit",
        tooltip: "Editar precio",
        color: "dark",
        onClick: (item) => handleEditClick(item.id, item.precio),
    }
];


